import * as React from "react";
import { Loader2 } from "lucide-react";
import { SafeLucideIcon } from "@/lib/icons/safeLucideIcon";
import { cn } from "@/lib/utils";
import { Button, buttonVariants, type ButtonProps } from "@/components/ui/button";
import { SmartLink as Link } from "@/components/store/shared/SmartLink";
import { useStorePath } from "@/hooks/useStorePath";
import { useOptionalStorefront } from "@/components/store/shared/StorefrontContext";
import { useTheme } from "@/storefront/theme/ThemeContext";
import { storefrontShadcnCtaChrome } from "@/storefront/theme/uiTokens";
import { useReferenceRuntimeChrome } from "@/storefront/context/ReferenceRuntimeChromeContext";
import { analyticsService, getFunnelSessionId } from "@/services/analytics";
import { useInternalCtaPrefetch } from "@/hooks/useSectionCtaPrefetch";
import { useCtaViewportImpression } from "@/hooks/useCtaViewportImpression";
import type { SectionButtonConfig } from "@/lib/sections/sectionButtonConfig";
import {
    dedupeSectionButtons,
    sectionButtonDedupeKey,
} from "@/lib/sections/sectionButtonConfig";
import {
    classifySectionButtonLink,
    hrefForSectionButton,
    sanitizeSectionButtonHref,
    shouldOpenLinkInNewTab,
    shouldUseRouterForSectionLink,
    type SectionLinkClassification,
} from "@/lib/sections/sectionButtonLink";
import { resolveInternalLink } from "@/lib/storefront/pageIntegrity";
import { resolveSafeLink } from "@/lib/storefront/resolveSafeLink";
import { useCoachCtaSectionId } from "@/pages/store-editor-shared/guided/CoachCtaSectionContext";
import { emitCoachNotify } from "@/pages/store-editor-shared/guided/coachNotifyBridge";

function mapVariant(v: SectionButtonConfig["variant"]): ButtonProps["variant"] {
    switch (v) {
        case "primary":
            return "default";
        case "secondary":
            return "secondary";
        case "outline":
            return "outline";
        case "ghost":
            return "ghost";
        default:
            return "default";
    }
}

function mapSize(s: SectionButtonConfig["size"]): ButtonProps["size"] {
    switch (s) {
        case "sm":
            return "sm";
        case "lg":
            return "lg";
        case "md":
        default:
            return "default";
    }
}

function ctaAccessibleLabel(
    text: string,
    classification: SectionLinkClassification,
    targetBlank: boolean
): string {
    const t = text.trim();
    if (!targetBlank) return t;
    if (
        classification.kind === "tel" ||
        classification.kind === "mailto" ||
        classification.kind === "internal_hash"
    ) {
        return t;
    }
    return `${t} (s'ouvre dans un nouvel onglet)`;
}

export interface SectionCtaTrackContext {
    sectionType: string;
    position?: number;
    storeId?: string;
    sessionId?: string;
}

export interface SectionConfigButtonProps {
    config: SectionButtonConfig;
    className?: string;
    /** Applied to the clickable root (link, anchor, or button). */
    style?: React.CSSProperties;
    loading?: boolean;
    disabled?: boolean;
    ariaLabel?: string;
    /** Editor / preview: resolve internal links against draft `pages` (unsaved state). */
    layoutResolution?: { pages?: Record<string, unknown> };
    /** Logo-only and similar: custom inner content (still uses one link pipeline). */
    customContent?: React.ReactNode;
    trackContext?: SectionCtaTrackContext;
    /** Viewport impression for CTR (default: on when `trackContext` + store/session exist). */
    trackImpression?: boolean;
    /** Inside `SectionConfigButtonRow` — skip `mx-auto` so flex `justify-*` controls layout. */
    layoutRowItem?: boolean;
    /** Editor / special flows: intercept navigation instead of using router/anchor. */
    navigateOverride?: (rawLink: string) => void;
}

function sectionConfigButtonPropsEqual(
    a: SectionConfigButtonProps,
    b: SectionConfigButtonProps
): boolean {
    const ca = a.config;
    const cb = b.config;
    return (
        ca.text === cb.text &&
        ca.link === cb.link &&
        ca.variant === cb.variant &&
        ca.size === cb.size &&
        ca.align === cb.align &&
        ca.fullWidth === cb.fullWidth &&
        ca.icon === cb.icon &&
        ca.openInNewTab === cb.openInNewTab &&
        ca.disabled === cb.disabled &&
        ca.loading === cb.loading &&
        a.loading === b.loading &&
        a.disabled === b.disabled &&
        a.className === b.className &&
        a.style === b.style &&
        a.ariaLabel === b.ariaLabel &&
        a.trackImpression === b.trackImpression &&
        a.layoutRowItem === b.layoutRowItem &&
        a.navigateOverride === b.navigateOverride &&
        a.layoutResolution === b.layoutResolution &&
        a.customContent === b.customContent &&
        a.trackContext?.sectionType === b.trackContext?.sectionType &&
        a.trackContext?.position === b.trackContext?.position &&
        a.trackContext?.storeId === b.trackContext?.storeId &&
        a.trackContext?.sessionId === b.trackContext?.sessionId
    );
}

function SectionConfigButtonInner({
    config,
    className,
    style,
    loading: loadingProp,
    disabled: disabledProp,
    ariaLabel: ariaLabelProp,
    layoutResolution,
    customContent,
    trackContext,
    trackImpression,
    layoutRowItem,
    navigateOverride,
}: SectionConfigButtonProps) {
    const coachSectionId = useCoachCtaSectionId();
    const { getStorePath } = useStorePath();
    const optionalStore = useOptionalStorefront();
    const refChrome = useReferenceRuntimeChrome();
    const refTheme = useTheme();
    const [impressionEl, setImpressionEl] = React.useState<HTMLSpanElement | null>(null);

    const pagesForResolve =
        layoutResolution?.pages ?? optionalStore?.template?.pages;
    const layoutForResolve = React.useMemo(
        () => (pagesForResolve ? { pages: pagesForResolve as Record<string, unknown> } : undefined),
        [pagesForResolve]
    );

    const normalizedLink = React.useMemo(() => resolveSafeLink(config.link), [config.link]);

    const effectiveLink = React.useMemo(
        () => resolveInternalLink(normalizedLink, layoutForResolve),
        [normalizedLink, layoutForResolve]
    );

    const hasVisibleText = Boolean(config.text?.trim());
    const fallbackA11y = ariaLabelProp?.trim();
    const hasCustom = customContent != null;
    const renderNull = !hasVisibleText && !fallbackA11y && !hasCustom;

    const loading = Boolean(loadingProp ?? config.loading);
    const disabled = Boolean(disabledProp ?? config.disabled);

    const sanitized = sanitizeSectionButtonHref(effectiveLink);
    const classification = classifySectionButtonLink(sanitized === "#" ? "#" : sanitized);
    const href = hrefForSectionButton(effectiveLink, getStorePath);
    const useRouter = shouldUseRouterForSectionLink(effectiveLink);
    const targetBlank = shouldOpenLinkInNewTab(sanitized, config.openInNewTab, classification);

    const storeId = trackContext?.storeId ?? optionalStore?.store?.id;
    const sessionId = trackContext?.sessionId ?? getFunnelSessionId(storeId);

    const scheduleAnalytics = React.useCallback(() => {
        if (!trackContext?.sectionType || !storeId || !sessionId) return;
        queueMicrotask(() => {
            void analyticsService.trackEvent(storeId, sessionId, "cta_click", {
                text: config.text || fallbackA11y || "",
                link: effectiveLink,
                section_type: trackContext.sectionType,
                position: trackContext.position ?? 0,
            });
        });
    }, [trackContext, storeId, sessionId, config.text, effectiveLink, fallbackA11y]);

    const scheduleImpression = React.useCallback(() => {
        if (!trackContext?.sectionType || !storeId || !sessionId) return;
        queueMicrotask(() => {
            void analyticsService.trackEvent(storeId, sessionId, "cta_impression", {
                text: config.text || fallbackA11y || "",
                link: effectiveLink,
                section_type: trackContext.sectionType,
                position: trackContext.position ?? 0,
            });
        });
    }, [trackContext, storeId, sessionId, config.text, effectiveLink, fallbackA11y]);

    const impressionEnabled =
        !renderNull &&
        trackImpression !== false &&
        Boolean(trackContext?.sectionType && storeId && sessionId && !loading && !disabled);

    useCtaViewportImpression(impressionEl, {
        enabled: impressionEnabled,
        onImpression: scheduleImpression,
    });

    const { onPrefetchIntent } = useInternalCtaPrefetch(
        href,
        useRouter && !loading && !disabled && !renderNull
    );

    const variant = mapVariant(config.variant);
    const size = mapSize(config.size);

    const layoutClass = cn(
        config.fullWidth && "w-full",
        !layoutRowItem &&
            !config.fullWidth &&
            config.align === "center" &&
            "mx-auto",
        !layoutRowItem && !config.fullWidth && config.align === "right" && "ml-auto",
        !layoutRowItem && !config.fullWidth && config.align === "left" && "mr-auto",
        className
    );

    const labelText = config.text?.trim() ?? "";
    const accessibleName =
        ariaLabelProp?.trim() ||
        (loading
            ? `${labelText || fallbackA11y || "Action"}, chargement en cours`
            : labelText
              ? ctaAccessibleLabel(labelText, classification, targetBlank)
              : (fallbackA11y ?? "Action"));

    const content =
        customContent ?? (
            <>
                {config.icon ? <SafeLucideIcon name={config.icon} className="h-4 w-4 shrink-0" /> : null}
                {config.text}
            </>
        );

    const wrapIfTracking = (node: React.ReactElement) =>
        impressionEnabled && !renderNull ? (
            <span ref={setImpressionEl} className="inline-flex max-w-full">
                {node}
            </span>
        ) : (
            node
        );

    if (renderNull) return null;

    const rawConfiguredLink = String(config.link ?? "").trim();
    const hasNoNavigationTarget =
        !navigateOverride &&
        !loading &&
        !disabled &&
        sanitized === "#" &&
        (!rawConfiguredLink || rawConfiguredLink === "#");

    if (hasNoNavigationTarget) {
        return wrapIfTracking(
            <Button
                type="button"
                variant={variant}
                size={size}
                className={cn(layoutClass, refChrome ? storefrontShadcnCtaChrome(refTheme) : null)}
                style={style}
                disabled
                aria-disabled
                aria-label={accessibleName}
            >
                {content}
            </Button>
        );
    }

    if (disabled || loading) {
        const btn = (
            <Button
                type="button"
                variant={variant}
                size={size}
                className={cn(layoutClass, refChrome ? storefrontShadcnCtaChrome(refTheme) : null)}
                style={style}
                disabled
                aria-busy={loading ? true : undefined}
                aria-label={accessibleName}
            >
                {loading ? (
                    <>
                        <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
                        {hasVisibleText ? config.text : hasCustom ? content : null}
                    </>
                ) : (
                    content
                )}
            </Button>
        );
        return wrapIfTracking(btn);
    }

    const anchorClass = cn(
        buttonVariants({ variant, size }),
        "theme-transition active:scale-95 inline-flex max-w-full",
        refChrome ? storefrontShadcnCtaChrome(refTheme) : null,
        layoutClass
    );

    const onNavigateClick = React.useCallback(() => {
        scheduleAnalytics();
    }, [scheduleAnalytics]);

    const reportCoachCtaInteraction = React.useCallback(() => {
        if (!coachSectionId) return;
        const raw = String(config.link ?? "").trim();
        const invalidLink =
            classification.kind === "invalid" ||
            (sanitized === "#" && (!raw || raw === "#"));
        if (invalidLink) {
            emitCoachNotify("FIX_LINK", "🔗 Complétez le lien de ce bouton", { sectionId: coachSectionId });
            return;
        }
        if (
            classification.kind === "external_http" ||
            classification.kind === "protocol_relative" ||
            classification.kind === "whatsapp"
        ) {
            emitCoachNotify("FIX_CTA", `🌐 Lien externe — ${labelText || "CTA"}`, { sectionId: coachSectionId });
            return;
        }
        emitCoachNotify("FIX_CTA", `🚀 CTA « ${labelText || "Action"} »`, { sectionId: coachSectionId });
    }, [coachSectionId, classification.kind, config.link, labelText, sanitized]);

    const clickHandler = React.useCallback(() => {
        reportCoachCtaInteraction();
        if (trackContext?.sectionType) onNavigateClick();
    }, [reportCoachCtaInteraction, trackContext?.sectionType, onNavigateClick]);

    const onHashAnchorClick = React.useCallback(
        (e: React.MouseEvent<HTMLAnchorElement>) => {
            if (classification.kind !== "internal_hash" || !href.startsWith("#") || href.length <= 1) {
                if (trackContext?.sectionType || coachSectionId) clickHandler();
                return;
            }
            const id = decodeURIComponent(href.slice(1).split("?")[0] ?? "");
            if (!id) {
                if (trackContext?.sectionType || coachSectionId) clickHandler();
                return;
            }
            const el = document.getElementById(id);
            if (el) {
                e.preventDefault();
                el.scrollIntoView({ behavior: "smooth", block: "start" });
            }
            if (trackContext?.sectionType || coachSectionId) clickHandler();
        },
        [classification.kind, href, trackContext?.sectionType, coachSectionId, clickHandler]
    );

    if (navigateOverride) {
        return wrapIfTracking(
            <button
                type="button"
                className={anchorClass}
                style={style}
                aria-label={accessibleName}
                onClick={(e) => {
                    e.preventDefault();
                    reportCoachCtaInteraction();
                    if (trackContext?.sectionType) scheduleAnalytics();
                    navigateOverride(effectiveLink);
                }}
            >
                {content}
            </button>
        );
    }

    if (useRouter) {
        return wrapIfTracking(
            <Link
                to={href}
                className={anchorClass}
                style={style}
                target={targetBlank ? "_blank" : undefined}
                rel={targetBlank ? "noopener noreferrer" : undefined}
                aria-label={accessibleName}
                onClick={trackContext?.sectionType || coachSectionId ? clickHandler : undefined}
                onMouseEnter={onPrefetchIntent}
                onFocus={onPrefetchIntent}
            >
                {content}
            </Link>
        );
    }

    return wrapIfTracking(
        <a
            href={href}
            className={anchorClass}
            style={style}
            target={targetBlank ? "_blank" : undefined}
            rel={targetBlank ? "noopener noreferrer" : undefined}
            aria-label={accessibleName}
            onClick={
                classification.kind === "internal_hash"
                    ? onHashAnchorClick
                    : trackContext?.sectionType || coachSectionId
                      ? clickHandler
                      : undefined
            }
        >
            {content}
        </a>
    );
}

export const SectionConfigButton = React.memo(SectionConfigButtonInner, sectionConfigButtonPropsEqual);

const GAP_PRESET = {
    sm: "gap-2",
    md: "gap-3",
    lg: "gap-4",
} as const;

export interface SectionConfigButtonRowProps {
    buttons: SectionButtonConfig[];
    rowAlign?: SectionButtonConfig["align"];
    className?: string;
    gapClassName?: string;
    gap?: keyof typeof GAP_PRESET;
    stackOnMobile?: boolean;
    trackContext?: Omit<SectionCtaTrackContext, "position">;
    trackImpression?: boolean;
    navigateOverride?: (rawLink: string) => void;
    layoutResolution?: { pages?: Record<string, unknown> };
}

function sectionConfigButtonRowPropsEqual(
    a: SectionConfigButtonRowProps,
    b: SectionConfigButtonRowProps
): boolean {
    return (
        a.buttons === b.buttons &&
        a.rowAlign === b.rowAlign &&
        a.className === b.className &&
        a.gapClassName === b.gapClassName &&
        a.gap === b.gap &&
        a.stackOnMobile === b.stackOnMobile &&
        a.trackImpression === b.trackImpression &&
        a.navigateOverride === b.navigateOverride &&
        a.layoutResolution === b.layoutResolution &&
        a.trackContext?.sectionType === b.trackContext?.sectionType &&
        a.trackContext?.storeId === b.trackContext?.storeId &&
        a.trackContext?.sessionId === b.trackContext?.sessionId
    );
}

function SectionConfigButtonRowInner({
    buttons,
    rowAlign = "center",
    className,
    gapClassName,
    gap = "md",
    stackOnMobile = false,
    trackContext,
    trackImpression,
    navigateOverride,
    layoutResolution,
}: SectionConfigButtonRowProps) {
    const items = React.useMemo(
        () => dedupeSectionButtons(buttons.filter((b) => b.text?.trim())),
        [buttons]
    );
    if (items.length === 0) return null;

    const justify =
        rowAlign === "left"
            ? "justify-start"
            : rowAlign === "right"
              ? "justify-end"
              : "justify-center";

    const gapCls = gapClassName ?? GAP_PRESET[gap] ?? GAP_PRESET.md;

    return (
        <div
            className={cn(
                "sf-section-cta-row flex",
                stackOnMobile ? "flex-col sm:flex-row sm:flex-wrap sm:items-center" : "flex-row flex-wrap items-center",
                gapCls,
                justify,
                stackOnMobile && "items-stretch sm:items-center",
                className
            )}
        >
            {items.map((btn, i) => (
                <SectionConfigButton
                    key={`${sectionButtonDedupeKey(btn.text, btn.link)}-${i}`}
                    config={btn}
                    layoutRowItem
                    layoutResolution={layoutResolution}
                    trackImpression={trackImpression}
                    navigateOverride={navigateOverride}
                    trackContext={
                        trackContext
                            ? { ...trackContext, position: i }
                            : undefined
                    }
                />
            ))}
        </div>
    );
}

export const SectionConfigButtonRow = React.memo(
    SectionConfigButtonRowInner,
    sectionConfigButtonRowPropsEqual
);
